import { Component } from '@grafikr/components';
import { fromEvent, map, pairwise, startWith, throttleTime } from 'rxjs';

import { navigationTargetHandle } from 'utils/navigation-target-handle';
import { NonNullish } from 'utils/nullish-check';

import { initHeaderDrawerMenuBlocks } from './header-drawer-menu-blocks';
import { initMegaMenuUnifiedHeight } from './header-mega-unified-height';

const MEGA_MENU_BLOCK_ITEM_CLASS = 'header__mega-menu-block-item';

export default Component((node, { app, onMounted }) => {
  const headerGroupElements = Array.from(
    document.querySelectorAll('.shopify-section-group-header-group'),
  );

  const isSticky = node.hasAttribute('data-sticky');
  const isTransparent = node.hasAttribute('data-transparent');
  let isHidden = false;
  let isAboveThreshold = false;
  let megaRevealsSolid = false;

  const getScrollThreshold = () =>
    headerGroupElements.reduce((acc, current) => acc + current.clientHeight, 0) +
    NonNullish(node.querySelector('[data-main]')).clientHeight;

  const syncHeaderTopOffset = () => {
    const shouldOffset = isTransparent && !isAboveThreshold && !megaRevealsSolid && !isHidden;
    const mainEl = node.querySelector('[data-main]');
    const height = mainEl ? mainEl.clientHeight : node.clientHeight;
    document.documentElement.style.setProperty(
      '--header-top-offset',
      shouldOffset ? `${height}px` : '0px',
    );
  };

  const syncTransparentClass = () => {
    node.classList.toggle(
      'header--transparent',
      isTransparent && !isAboveThreshold && !megaRevealsSolid,
    );
    syncHeaderTopOffset();
  };

  const isMegaColumnSubstantive = (li: Element) =>
    Boolean(
      li.textContent?.trim() ||
      li.querySelector('a[href]') ||
      li.querySelector('img, svg, picture') ||
      li.querySelector('ul li'),
    );

  const pruneEmptyMegaMenus = () => {
    node.querySelectorAll<HTMLLIElement>('.header__menu-root-item').forEach((rootLi) => {
      const mega = rootLi.querySelector<HTMLElement>(':scope > [data-header-mega-menu]');
      if (!mega) return;

      const columnList =
        mega.querySelector<HTMLUListElement>('.header__mega-menu-row') ??
        mega.querySelector<HTMLUListElement>('.header__mega-menu ul');
      if (!columnList) {
        mega.remove();
        return;
      }

      Array.from(columnList.querySelectorAll<HTMLLIElement>(':scope > li')).forEach((li) => {
        if (!isMegaColumnSubstantive(li)) {
          li.remove();
        }
      });

      if (!columnList.querySelector(':scope > li')) {
        mega.remove();
      }
    });
  };

  const handleMenuBlocks = () => {
    const cardLiClass = `min-w-0 flex-1 flex min-h-0 flex-col ${MEGA_MENU_BLOCK_ITEM_CLASS}`;

    const resolveMenuBlockTemplate = (
      menuBlockTarget: string,
      rootLinkTitle?: string,
    ): HTMLTemplateElement | null => {
      const candidates = [
        menuBlockTarget,
        ...(rootLinkTitle ? [navigationTargetHandle(rootLinkTitle)] : []),
      ].filter((value, index, array) => value.length > 0 && array.indexOf(value) === index);

      for (const candidate of candidates) {
        const match = node.querySelector<HTMLTemplateElement>(
          `template[data-target="${candidate}"]`,
        );
        if (match) return match;
      }

      return null;
    };

    const getTopLevelFromFragment = (fragment: DocumentFragment): Element[] => {
      const direct = Array.from(fragment.children) as Element[];
      if (direct.length === 1) {
        const [first] = direct;
        if (first && first.tagName === 'DIV' && first.id.startsWith('shopify-block-')) {
          return Array.from((first as HTMLElement).children) as Element[];
        }
      }
      return direct;
    };

    const blocks = node.querySelectorAll<HTMLLIElement>('[data-menu-block]');

    blocks.forEach((placeholder) => {
      const target = NonNullish(placeholder.dataset.menuBlock);
      const rootLinkTitle = placeholder
        .closest('.header__menu-root-item')
        ?.querySelector(':scope > a')
        ?.textContent?.trim();
      const template = resolveMenuBlockTemplate(target, rootLinkTitle);
      const shopifyBlockId = template?.dataset.shopifyBlockId;

      if (template) {
        const source = document.importNode(template.content, true);
        const topLevel = getTopLevelFromFragment(source).filter(
          (el) =>
            el.hasAttribute('data-header-mega-menu-item') ||
            !el.hasAttribute('data-header-drawer-nav-item'),
        );

        if (topLevel.length === 0) {
          placeholder.remove();
          return;
        }

        topLevel.forEach((wrapper, i) => {
          const el = wrapper.hasAttribute('data-header-mega-menu-item')
            ? wrapper.firstElementChild
            : wrapper;
          if (!el) return;

          const li = document.createElement('li');
          li.className = cardLiClass;
          if (i === 0 && shopifyBlockId) {
            li.id = shopifyBlockId;
          }
          li.append(el);
          placeholder.insertAdjacentElement('beforebegin', li);
        });
        placeholder.remove();
      } else {
        placeholder.remove();
      }
    });
  };

  const syncMegaBackdropTop = () => {
    node.style.setProperty('--header-mega-backdrop-top', `${node.getBoundingClientRect().top}px`);
  };

  const setHeightProperty = () => {
    const mainEl = node.querySelector('[data-main]');
    const height = mainEl ? mainEl.clientHeight : node.clientHeight;
    document.documentElement.style.setProperty(
      '--header-height',
      !isHidden ? `${height}px` : '0px',
    );
    syncMegaBackdropTop();
    syncHeaderTopOffset();
  };

  const handleScroll = () => {
    fromEvent(window, 'scroll', { passive: true })
      .pipe(
        throttleTime(50),
        map(() => window.scrollY),
        startWith(window.scrollY),
        pairwise(),
        map(([previous, current]) => {
          const threshold = getScrollThreshold();
          return {
            shouldHide: current > threshold && current > previous,
            isAboveThreshold: current > threshold,
          };
        }),
      )
      .subscribe(({ shouldHide, isAboveThreshold: above }) => {
        isAboveThreshold = above;
        node.classList.toggle('header--hidden', isSticky && shouldHide);
        syncTransparentClass();
        isHidden = shouldHide;
        setHeightProperty();
      });
  };

  const handleResize = () => {
    new ResizeObserver(setHeightProperty).observe(node);
    window.addEventListener('resize', syncMegaBackdropTop, { passive: true });
  };

  const assignMegaStagger = (rootLi: HTMLElement) => {
    const row = rootLi.querySelector<HTMLElement>('.header__mega-menu-row');
    const items = row ? Array.from(row.querySelectorAll<HTMLElement>(':scope > li')) : [];
    items.forEach((li, i) => {
      li.style.setProperty('--mega-stagger', String(i));
    });
  };

  const handleDesktopMegaMenus = () => {
    const nav = node.querySelector<HTMLElement>('[data-main] nav');
    if (!nav) return;

    const HOVER_DEBOUNCE_MS = 175;
    let activeRoot: HTMLElement | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const hasMega = (rootLi: HTMLElement) =>
      Boolean(rootLi.querySelector(':scope > [data-header-mega-menu]'));

    const syncMegaBackdrop = () => {
      node.classList.toggle('header--mega-backdrop', activeRoot !== null);
      syncMegaBackdropTop();
    };

    const setActiveRoot = (rootLi: HTMLElement | null) => {
      if (activeRoot === rootLi) return;

      const prevRoot = activeRoot;
      activeRoot = rootLi;

      if (prevRoot) {
        prevRoot.classList.remove('header__menu-root-item--mega-active');
      }

      if (rootLi) {
        if (prevRoot !== null) {
          rootLi.classList.add('header__menu-root-item--mega-cross');
          requestAnimationFrame(() => {
            requestAnimationFrame(() =>
              rootLi.classList.remove('header__menu-root-item--mega-cross'),
            );
          });
        }
        assignMegaStagger(rootLi);
        rootLi.classList.add('header__menu-root-item--mega-active');
      }

      megaRevealsSolid = Boolean(rootLi && hasMega(rootLi));
      syncTransparentClass();
      syncMegaBackdrop();
    };

    const cancelDebounce = () => {
      if (debounceTimer !== null) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
    };

    const scheduleActivation = (rootLi: HTMLElement) => {
      cancelDebounce();
      debounceTimer = setTimeout(() => {
        debounceTimer = null;
        setActiveRoot(hasMega(rootLi) ? rootLi : null);
      }, HOVER_DEBOUNCE_MS);
    };

    nav.querySelectorAll<HTMLElement>('.header__menu-root-item').forEach((rootLi) => {
      rootLi.addEventListener('pointerenter', () => {
        scheduleActivation(rootLi);
      });
    });

    nav.addEventListener('pointerleave', (e) => {
      const related = e.relatedTarget;
      if (related instanceof Node && nav.contains(related)) return;
      cancelDebounce();
      setActiveRoot(null);
    });

    nav.addEventListener('focusin', (e) => {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const rootLi = t.closest<HTMLElement>('.header__menu-root-item');
      if (!rootLi || !nav.contains(rootLi)) return;
      cancelDebounce();
      setActiveRoot(hasMega(rootLi) ? rootLi : null);
    });

    nav.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (nav.contains(document.activeElement)) return;
        cancelDebounce();
        setActiveRoot(null);
      }, 0);
    });
  };

  onMounted(() => {
    handleMenuBlocks();
    initHeaderDrawerMenuBlocks(node);
    pruneEmptyMegaMenus();
    initMegaMenuUnifiedHeight(node);
    handleDesktopMegaMenus();
    app.mount(node);

    const drawer = document.querySelector<HTMLElement>('[data-load-target="header-drawer"]');
    if (drawer) {
      app.mount(drawer);
    }

    handleResize();

    isAboveThreshold = window.scrollY > getScrollThreshold();
    syncTransparentClass();
    syncMegaBackdropTop();

    if (isSticky) {
      handleScroll();
    } else {
      fromEvent(window, 'scroll', { passive: true })
        .pipe(throttleTime(50))
        .subscribe(syncMegaBackdropTop);
    }
  });
});
